import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

/**
 * Popup de mise à jour affichée en bas à gauche du site.
 * Invite l'utilisateur à actualiser pour vider le cache et récupérer les
 * dernières modifications. Le rechargement n'est jamais forcé : l'utilisateur
 * choisit d'actualiser maintenant ou plus tard.
 */
const UpdateNotification = ({ onUpdate }) => {
    const [isDarkMode, setIsDarkMode] = useState(() => {
        if (typeof window !== 'undefined') {
            return document.documentElement.classList.contains('dark');
        }
        return false;
    });
    const [isVisible, setIsVisible] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const [isReloading, setIsReloading] = useState(false);
    const isMobile = useMediaQuery({ maxWidth: 640 });

    // Suivre le mode sombre
    useEffect(() => {
        const htmlElement = document.documentElement;
        const checkDarkMode = () => setIsDarkMode(htmlElement.classList.contains('dark'));
        checkDarkMode();

        const observer = new MutationObserver(checkDarkMode);
        observer.observe(htmlElement, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    // Petite animation d'entrée
    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 50);
        return () => clearTimeout(timer);
    }, []);

    const handleUpdate = () => {
        setIsReloading(true);
        onUpdate();
    };

    const handleDismiss = () => {
        setIsVisible(false);
        // Laisse l'animation de sortie se jouer avant de démonter
        setTimeout(() => setIsDismissed(true), 250);
    };

    if (isDismissed) return null;

    const surface = isDarkMode
        ? 'bg-[#23262F] border border-white/10 text-blanc'
        : 'bg-blanc border border-gris-30 text-noir';
    const secondaryText = isDarkMode ? 'text-blanc-70' : 'text-noir-70';
    const ghostBtn = isDarkMode
        ? 'border border-white/15 text-blanc-70 hover:bg-white/10'
        : 'border border-gris-30 text-noir-70 hover:bg-gris-15';

    return (
        <div
            className={`
                fixed bottom-5 z-[9999]
                ${isMobile ? 'left-3 right-3' : 'left-5 w-[360px]'}
                ${surface}
                rounded-2xl
                shadow-[0_10px_40px_rgba(0,0,0,0.18)]
                transition-all duration-300 ease-out
                ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}
            `}
            role="status"
            aria-live="polite"
        >
            <div className="p-5">
                <div className="flex items-start gap-3">
                    {/* Icône info dans un carré sombre */}
                    <div
                        className={`
                            flex-shrink-0 flex items-center justify-center
                            w-8 h-8 rounded-lg
                            ${isDarkMode ? 'bg-white/15' : 'bg-emeraude'}
                        `}
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <circle cx="12" cy="12" r="9" stroke="#FFFFFF" strokeWidth="2.25" />
                            <path d="M12 11.25v4.75" stroke="#FFFFFF" strokeWidth="2.25" strokeLinecap="round" />
                            <circle cx="12" cy="8" r="1.3" fill="#FFFFFF" />
                        </svg>
                    </div>

                    {/* Textes */}
                    <div className="flex-1 min-w-0 pt-0.5">
                        <p className="poppins-semibold text-sm leading-snug">
                            Une nouvelle version est disponible
                        </p>
                        <p className={`poppins-regular text-xs leading-relaxed mt-0.5 ${secondaryText}`}>
                            Actualisez pour voir les dernières modifications.
                        </p>
                    </div>
                </div>

                {/* Actions */}
                <div className="mt-4 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={handleDismiss}
                        disabled={isReloading}
                        className={`
                            px-3.5 py-1.5 rounded-lg
                            poppins-medium text-xs font-semibold
                            ${ghostBtn}
                            active:scale-95 transition-all duration-200
                            focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gris/40
                            disabled:opacity-60 disabled:cursor-not-allowed
                        `}
                    >
                        Plus tard
                    </button>
                    <button
                        type="button"
                        onClick={handleUpdate}
                        disabled={isReloading}
                        className={`
                            px-3.5 py-1.5 rounded-lg
                            poppins-medium text-xs font-semibold
                            bg-emeraude text-[#04322F] hover:brightness-110
                            active:scale-95 transition-all duration-200
                            focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-emeraude/50
                            disabled:opacity-70 disabled:cursor-not-allowed
                            flex items-center gap-1.5
                        `}
                    >
                        {isReloading && (
                            <svg className="w-3.5 h-3.5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.3" />
                                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                            </svg>
                        )}
                        {isReloading ? 'Actualisation…' : 'Actualiser'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpdateNotification;
